import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { PermissionGuard } from "@/components/PermissionGuard";
import { MobileBlockerGate } from "@/components/MobileBlocker";
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  Connection,
  EdgeProps,
  ReactFlowProvider,
  getBezierPath,
  BaseEdge,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useRoute, useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Save, ArrowLeft, Undo2, Redo2, AlertTriangle, X, MessageSquare, Trash2,
  Play, Send, LayoutGrid, Plus, CheckCircle2,
} from 'lucide-react';
import ContentNode from '@/components/FlowBuilder/ContentNode';
import TestChatPanel from '@/components/FlowBuilder/TestChatPanel';
import TestSendDialog from '@/components/FlowBuilder/TestSendDialog';
import WebhookTriggerBanner from '@/components/FlowBuilder/WebhookTriggerBanner';
import ActionNode from '@/components/FlowBuilder/ActionNode';
import ConditionNode from '@/components/FlowBuilder/ConditionNode';
import DelayNode from '@/components/FlowBuilder/DelayNode';
import RandomizerNode from '@/components/FlowBuilder/RandomizerNode';
import TargetNode from '@/components/FlowBuilder/TargetNode';
import AIAssistantNode from '@/components/FlowBuilder/AIAssistantNode';
import AIClassifierNode from '@/components/FlowBuilder/AIClassifierNode';
import DataCollectionNode from '@/components/FlowBuilder/DataCollectionNode';
import WebhookNode from '@/components/FlowBuilder/WebhookNode';
import EndNode from '@/components/FlowBuilder/EndNode';
import NodeConfigPanel from '@/components/FlowBuilder/NodeConfigPanel';
import GatilhoNode from '@/components/FlowBuilder/GatilhoNode';
import { PaletaDeBlocos } from '@/components/FlowBuilder/PaletaDeBlocos';
import { PainelDeGatilho } from '@/components/FlowBuilder/PainelDeGatilho';
import { PainelLateral } from '@/components/FlowBuilder/PainelLateral';
import { PainelDePendencias, listarPendencias } from '@/components/FlowBuilder/PainelDePendencias';
import { BLOCOS_POR_CHAVE, SAIDA_PADRAO_POR_TIPO, type BlocoDaPaleta } from '@/components/FlowBuilder/catalogo-de-blocos';
import { organizarFluxo } from '@/components/FlowBuilder/auto-layout';
import { pendenciaDoGatilho } from '@shared/flow-triggers';
import { acharNoDeEntrada } from '@shared/flow-utils';
import { useContextoDaTela } from '@/features/assistente/contexto-de-tela';

const nodeTypes = {
  content: ContentNode,
  action: ActionNode,
  condition: ConditionNode,
  delay: DelayNode,
  randomizer: RandomizerNode,
  target: TargetNode,
  ai_assistant: AIAssistantNode,
  ai_classifier: AIClassifierNode,
  data_collection: DataCollectionNode,
  webhook_request: WebhookNode,
  end: EndNode,
  // Sintético: desenhado no canvas, nunca salvo em `nodes`. Ver GatilhoNode.
  __gatilho__: GatilhoNode,
};

/** Id do nó sintético do gatilho. Prefixado para nunca colidir com "node-N". */
const ID_DO_GATILHO = '__gatilho__';
/** Tem que bater com o cartão em GatilhoNode.tsx — ver nota em `cartaoDoGatilho`. */
const LARGURA_DO_GATILHO = 300;
const ALTURA_DO_GATILHO = 58;

/** Passado aos botões da aresta via `edge.data` — ver `ArestaComAcoes`. */
type AcoesDaAresta = {
  aoInserir: (edgeId: string) => void;
  aoExcluir: (edgeId: string) => void;
};

/**
 * Aresta com os dois gestos que faltavam: inserir um passo no meio e apagar
 * a ligação.
 *
 * O "X" existia aqui desde sempre — e era um `<button>` sem `onClick`. Quem
 * clicava via um botão de excluir que não excluía, e só descobria o menu de
 * contexto por acidente.
 */
function ArestaComAcoes({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style, markerEnd, data,
}: EdgeProps<AcoesDaAresta>) {
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <foreignObject
        width={56}
        height={24}
        x={labelX - 28}
        y={labelY - 12}
        className="overflow-visible"
        requiredExtensions="http://www.w3.org/1999/xhtml"
      >
        <div className="flex items-center justify-center gap-1 h-full opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <button
            type="button"
            className="w-5 h-5 rounded-full bg-white border border-border/60 shadow-sm flex items-center justify-center
                       text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-primary/5 cursor-pointer"
            title="Inserir um passo aqui"
            onClick={(e) => { e.stopPropagation(); data?.aoInserir(id); }}
          >
            <Plus className="w-3 h-3" />
          </button>
          <button
            type="button"
            className="w-5 h-5 rounded-full bg-white border border-border/60 shadow-sm flex items-center justify-center
                       text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-red-50 cursor-pointer"
            title="Excluir conexão"
            onClick={(e) => { e.stopPropagation(); data?.aoExcluir(id); }}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </foreignObject>
    </>
  );
}

const edgeTypes = { deletable: ArestaComAcoes };

type HistorySnapshot = { nodes: Node[]; edges: Edge[] };

export default function WhatsAppFlowBuilder() {
  return (
    <MobileBlockerGate featureName="Editor de Fluxos do WhatsApp">
      <ReactFlowProvider>
        <WhatsAppFlowBuilderImpl />
      </ReactFlowProvider>
    </MobileBlockerGate>
  );
}

function WhatsAppFlowBuilderImpl() {
  const [, params] = useRoute('/whatsapp-flows/:id');
  const [, navigate] = useLocation();
  const flowId = params?.id ? Number(params.id) : null;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [flowName, setFlowName] = useState('Novo Fluxo');
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  /** Aresta em que o próximo bloco da paleta vai ser encaixado. */
  const [inserirNaAresta, setInserirNaAresta] = useState<string | null>(null);
  const [painelDireito, setPainelDireito] = useState<
    'config' | 'pendencias' | 'paleta' | 'gatilho' | null
  >(null);

  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
  const [showTestChat, setShowTestChat] = useState(false);
  const [showTestSend, setShowTestSend] = useState(false);
  const [confirmLeaveOpen, setConfirmLeaveOpen] = useState(false);

  const historyRef = useRef<HistorySnapshot[]>([]);
  const futureRef = useRef<HistorySnapshot[]>([]);
  const nodeIdRef = useRef(0);
  const canvasRef = useRef<HTMLDivElement>(null);
  const blocoArrastadoRef = useRef<BlocoDaPaleta | null>(null);

  const { screenToFlowPosition, setCenter, fitView } = useReactFlow();
  const utils = trpc.useUtils();

  // ─── Carregar o fluxo ───────────────────────────────────────────────────────
  const { data: flow, isLoading } = trpc.flows.get.useQuery({ id: flowId! }, { enabled: !!flowId });

  const updateMutation = trpc.flows.update.useMutation({
    onSuccess: () => {
      utils.flows.list.invalidate();
      setHasUnsaved(false);
      toast.success('Fluxo salvo.');
    },
    onError: (err) => toast.error(toUserFriendlyError(err, 'Erro ao salvar')),
  });

  // O gatilho é configuração do fluxo, não desenho: grava sozinho, sem esperar
  // o Salvar do canvas. Mutation separada para não mandar `nodes`/`edges` junto
  // e reescrever o espelho do motor à toa.
  const gatilhoMutation = trpc.flows.update.useMutation({
    onSuccess: () => {
      utils.flows.get.invalidate({ id: flowId! });
      utils.flows.list.invalidate();
      toast.success('Gatilho atualizado.');
    },
    onError: (err) => toast.error(toUserFriendlyError(err, 'Erro ao salvar o gatilho')),
  });

  useEffect(() => {
    if (flow && !loaded) {
      setFlowName(flow.name);
      const savedNodes = (flow.nodes as Node[]) ?? [];
      const savedEdges = (flow.edges as Edge[]) ?? [];
      if (savedNodes.length > 0) {
        setNodes(savedNodes);
        setEdges(savedEdges);
        nodeIdRef.current =
          savedNodes.reduce((max, n) => Math.max(max, parseInt(n.id.replace('node-', '')) || 0), 0) + 1;
      }
      setLoaded(true);
    }
  }, [flow, loaded]);

  // ─── Guarda de saída ────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // ─── Histórico ──────────────────────────────────────────────────────────────
  const pushHistory = (currentNodes: Node[], currentEdges: Edge[]) => {
    historyRef.current = [...historyRef.current.slice(-49), { nodes: currentNodes, edges: currentEdges }];
    futureRef.current = [];
  };

  const undo = () => {
    const history = historyRef.current;
    if (history.length === 0) return;
    const prev = history[history.length - 1]!;
    historyRef.current = history.slice(0, -1);
    futureRef.current = [{ nodes: [...nodes], edges: [...edges] }, ...futureRef.current.slice(0, 49)];
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNode(null);
    setHasUnsaved(true);
  };

  const redo = () => {
    const future = futureRef.current;
    if (future.length === 0) return;
    const next = future[0]!;
    futureRef.current = future.slice(1);
    historyRef.current = [...historyRef.current.slice(-49), { nodes: [...nodes], edges: [...edges] }];
    setNodes(next.nodes);
    setEdges(next.edges);
    setSelectedNode(null);
    setHasUnsaved(true);
  };

  // ─── Ligações ───────────────────────────────────────────────────────────────
  const onConnect = useCallback(
    (connection: Connection) => {
      pushHistory([...nodes], [...edges]);
      const sourceHandle = connection.sourceHandle;
      let edgeLabel: string | undefined;
      if (sourceHandle?.startsWith('btn-')) {
        const sourceNode = nodes.find((n) => n.id === connection.source);
        const btn = sourceNode?.data.messageContent?.buttons?.find(
          (b: any) => b.id === sourceHandle.replace('btn-', ''),
        );
        if (btn) edgeLabel = btn.text;
      }
      setEdges((eds) =>
        addEdge({ ...connection, ...(edgeLabel ? { label: edgeLabel, style: { stroke: '#22c55e' } } : {}) }, eds),
      );
      setHasUnsaved(true);
    },
    [nodes, edges],
  );

  // ─── Adicionar blocos ───────────────────────────────────────────────────────
  const criarNo = (bloco: BlocoDaPaleta, position: { x: number; y: number }): Node => ({
    id: `node-${nodeIdRef.current++}`,
    type: bloco.tipo,
    position,
    data: { label: bloco.rotulo, ...(bloco.padroes ?? {}) },
  });

  /** Encaixa o bloco no fim da sequência, já ligado ao passo anterior. */
  const adicionarEmSequencia = (bloco: BlocoDaPaleta) => {
    pushHistory([...nodes], [...edges]);

    const ancora = selectedNode ?? nodes[nodes.length - 1] ?? null;
    const position = ancora
      ? { x: ancora.position.x + 320, y: ancora.position.y }
      : { x: 80, y: 120 };
    const novo = criarNo(bloco, position);

    setNodes((nds) => [...nds, novo]);

    // Só liga automaticamente quando a âncora tem uma saída padrão livre.
    // Condição, classificador e IA têm saídas nomeadas: adivinhar por qual
    // caminho o novo passo entra seria pior que deixar a pessoa ligar.
    // Só liga sozinho quando a âncora tem UMA saída óbvia. Condição,
    // classificador e IA têm saídas nomeadas: adivinhar por qual caminho o
    // novo passo entra seria pior que deixar a pessoa ligar.
    const ancoraTemSaidaUnica =
      !!ancora && SAIDA_PADRAO_POR_TIPO[ancora.type ?? ''] === 'default';
    const jaTemSaida = ancora && edges.some((e) => e.source === ancora.id && (!e.sourceHandle || e.sourceHandle === 'default'));
    if (ancora && ancoraTemSaidaUnica && !jaTemSaida) {
      setEdges((eds) => addEdge({ source: ancora.id, target: novo.id, sourceHandle: 'default', targetHandle: null }, eds));
    }

    setSelectedNode(novo);
    setPainelDireito('config');
    setHasUnsaved(true);
    // Sem isto o bloco nasce fora da área visível quando o fluxo já é longo:
    // a pessoa clica, o painel abre e o canvas não mexe — parece que nada
    // aconteceu.
    trazerParaAVista(novo);
  };

  /** Encaixa o bloco no meio de uma ligação, religando as duas pontas. */
  const inserirNoMeio = (bloco: BlocoDaPaleta, edgeId: string) => {
    const aresta = edges.find((e) => e.id === edgeId);
    if (!aresta) return;
    const origem = nodes.find((n) => n.id === aresta.source);
    const destino = nodes.find((n) => n.id === aresta.target);
    if (!origem || !destino) return;

    pushHistory([...nodes], [...edges]);

    const saidaDoBloco = SAIDA_PADRAO_POR_TIPO[bloco.tipo] ?? 'default';

    // Desloca para baixo do meio: com os dois vizinhos próximos, o bloco novo
    // nasceria por cima de um deles.
    const novo = criarNo(bloco, {
      x: (origem.position.x + destino.position.x) / 2,
      y: (origem.position.y + destino.position.y) / 2 + 200,
    });

    setNodes((nds) => [...nds, novo]);
    setEdges((eds) => [
      ...eds.filter((e) => e.id !== edgeId),
      // A primeira metade herda o rótulo e o handle da aresta original, para
      // que um caminho "Sim" continue sendo o "Sim" depois da inserção.
      {
        id: `e-${aresta.source}-${novo.id}-${Date.now()}`,
        source: aresta.source,
        target: novo.id,
        sourceHandle: aresta.sourceHandle,
        label: aresta.label,
        style: aresta.style,
      },
      // A saída depende do tipo: condição sai por `true`, randomizador por
      // `path1`, IA por `success`. Cravar `default` fazia o ReactFlow
      // descartar esta aresta do desenho e mantê-la no estado — a tela
      // mostrava um fluxo e o motor rodava outro.
      ...(saidaDoBloco === null
        ? []
        : [{
            id: `e-${novo.id}-${aresta.target}-${Date.now() + 1}`,
            source: novo.id,
            target: aresta.target,
            sourceHandle: saidaDoBloco,
          }]),
    ]);

    setInserirNaAresta(null);
    setSelectedNode(novo);
    setPainelDireito('config');
    setHasUnsaved(true);
    trazerParaAVista(novo);
  };

  const aoClicarNaPaleta = (bloco: BlocoDaPaleta) => {
    if (inserirNaAresta) inserirNoMeio(bloco, inserirNaAresta);
    else adicionarEmSequencia(bloco);
  };

  const aoSoltarNoCanvas = (event: React.DragEvent) => {
    event.preventDefault();
    const chave = event.dataTransfer.getData('application/leadconversa-bloco');
    const bloco = BLOCOS_POR_CHAVE.get(chave) ?? blocoArrastadoRef.current;
    blocoArrastadoRef.current = null;
    if (!bloco) return;

    pushHistory([...nodes], [...edges]);
    // Soltar no canvas é escolher a posição à mão: cancela o "inserir nesta
    // ligação" que porventura estivesse armado, senão o próximo clique na
    // paleta encaixaria numa aresta que a pessoa já esqueceu.
    setInserirNaAresta(null);
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const novo = criarNo(bloco, position);
    setNodes((nds) => [...nds, novo]);
    setSelectedNode(novo);
    setPainelDireito('config');
    setHasUnsaved(true);
  };

  // ─── Editar, apagar, organizar ──────────────────────────────────────────────
  const handleUpdateNode = (nodeId: string, data: any) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)));
    setSelectedNode((prev) => (prev ? { ...prev, data } : null));

    // Aresta pendurada em botão que não existe mais vira ligação fantasma.
    const idsDeBotao = new Set<string>(
      (data.messageContent?.buttons ?? []).map((b: any) => `btn-${b.id}`),
    );
    idsDeBotao.add('default');
    setEdges((eds) =>
      eds.filter((e) => {
        if (e.source !== nodeId) return true;
        if (!e.sourceHandle) return true;
        if (!e.sourceHandle.startsWith('btn-')) return true;
        return idsDeBotao.has(e.sourceHandle);
      }),
    );
    setHasUnsaved(true);
  };

  const confirmDeleteNode = () => {
    if (!nodeToDelete) return;
    pushHistory([...nodes], [...edges]);
    setNodes((nds) => nds.filter((n) => n.id !== nodeToDelete));
    setEdges((eds) => eds.filter((e) => e.source !== nodeToDelete && e.target !== nodeToDelete));
    if (selectedNode?.id === nodeToDelete) { setSelectedNode(null); setPainelDireito(null); }
    setHasUnsaved(true);
    setNodeToDelete(null);
  };

  const excluirAresta = useCallback((edgeId: string) => {
    pushHistory([...nodes], [...edges]);
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setHasUnsaved(true);
  }, [nodes, edges]);

  const organizar = () => {
    if (nodes.length === 0) return;
    pushHistory([...nodes], [...edges]);
    setNodes(organizarFluxo(nodes, edges));
    setHasUnsaved(true);
    // O fitView tem que rodar depois do React aplicar as posições novas.
    setTimeout(() => fitView({ padding: 0.2, duration: 400 }), 0);
  };

  /** Centraliza a câmera num bloco. O timeout deixa o React posicionar antes. */
  const trazerParaAVista = (no: Node) => {
    setTimeout(() => setCenter(no.position.x + 120, no.position.y + 70, { zoom: 1, duration: 350 }), 0);
  };

  const irParaNo = (noId: string) => {
    const no = nodes.find((n) => n.id === noId);
    if (!no) return;
    setCenter(no.position.x + 110, no.position.y + 60, { zoom: 1, duration: 400 });
    setSelectedNode(no);
    setPainelDireito('config');
  };

  // ─── Pendências ─────────────────────────────────────────────────────────────
  // Só busca quando o gatilho é de etiqueta — as demais telas não precisam.
  const etiquetasDoGatilho = trpc.tags.list.useQuery(undefined, {
    enabled: flow?.triggerType === 'tag_added',
    retry: false,
  });
  const listaDeEtiquetas = etiquetasDoGatilho.data as Array<{ id: number; name: string }> | undefined;
  const nomeDaEtiquetaDoGatilho =
    listaDeEtiquetas?.find(
      (t) =>
        String(t.id) === String(flow?.triggerValue ?? '') ||
        t.name.toLowerCase() === String(flow?.triggerValue ?? '').trim().toLowerCase(),
    )?.name ?? null;
  /**
   * `false` só quando temos a lista em mãos e a etiqueta não está nela — antes
   * de carregar, `undefined` evita acusar o que ainda não sabemos.
   *
   * Casa por id E por nome: fluxos antigos guardaram o NOME da etiqueta, e o
   * espelhamento resolve isso no servidor (`idDaEtiqueta`). Comparar só ids
   * acusaria "Etiqueta não existe mais" num fluxo que funciona — e, pior,
   * travaria o Salvar com uma mensagem falsa.
   */
  const etiquetaDoGatilhoExiste =
    flow?.triggerType === 'tag_added' && listaDeEtiquetas
      ? listaDeEtiquetas.some(
          (t) =>
            String(t.id) === String(flow?.triggerValue ?? '') ||
            t.name.toLowerCase() === String(flow?.triggerValue ?? '').trim().toLowerCase(),
        )
      : undefined;

  const pendencias = useMemo(
    () =>
      listarPendencias(flowName, nodes, edges, {
        tipo: flow?.triggerType,
        valor: flow?.triggerValue,
        etiquetaExiste: etiquetaDoGatilhoExiste,
      }),
    [flowName, nodes, edges, flow?.triggerType, flow?.triggerValue, etiquetaDoGatilhoExiste],
  );
  // Só o que faria o motor falhar impede de salvar. Bloco ainda não ligado é
  // aviso: quem monta um fluxo grande salva no meio do caminho.
  const bloqueios = pendencias.filter((p) => p.bloqueia);

  // ─── Cartão "Começa quando" ─────────────────────────────────────────────────
  // Hooks: ficam aqui em cima porque abaixo há um `return` de carregamento —
  // declarar useMemo depois dele muda a ordem dos hooks entre renders.
  const noDeEntrada = useMemo(() => acharNoDeEntrada(nodes, edges) ?? null, [nodes, edges]);

  const cartaoDoGatilho: Node = useMemo(
    () => ({
      id: ID_DO_GATILHO,
      type: '__gatilho__',
      draggable: false,
      deletable: false,
      selectable: false,
      // Sem isto o ReactFlow embrulha o cartão num elemento `role="button"`
      // com tabindex, para permitir selecionar o nó pelo teclado. Como este
      // não é selecionável, o resultado é um botão dentro de outro: o leitor
      // de tela anuncia o cartão inteiro como um controle e o "Alterar" de
      // verdade some da árvore de acessibilidade.
      focusable: false,
      // Tamanho declarado de propósito. Os nós de verdade têm as dimensões
      // medidas escritas de volta no estado por `onNodesChange`; este é
      // sintético e nasce de novo a cada render, então nunca recebe essa
      // devolução — sem width/height o ReactFlow o mantém em
      // `visibility: hidden`, esperando uma medição que não chega.
      width: LARGURA_DO_GATILHO,
      height: ALTURA_DO_GATILHO,
      position: noDeEntrada
        ? { x: noDeEntrada.position.x - 380, y: noDeEntrada.position.y }
        : { x: 80, y: 140 },
      data: {
        triggerType: flow?.triggerType,
        triggerValue: flow?.triggerValue,
        // O gatilho de etiqueta guarda o ID; o nome vem daqui para o cartão
        // não mostrar "a etiqueta 7".
        nomeDaEtiqueta: nomeDaEtiquetaDoGatilho,
        aoAlterar: () => { setSelectedNode(null); setPainelDireito('gatilho'); },
      },
    }),
    [noDeEntrada, flow?.triggerType, flow?.triggerValue, nomeDaEtiquetaDoGatilho],
  );


  // ─── Salvar ─────────────────────────────────────────────────────────────────
  const saveFlowAsync = async (): Promise<boolean> => {
    if (!flowId) {
      toast.error('Nenhum fluxo selecionado para salvar.');
      return false;
    }
    if (bloqueios.length > 0) {
      setPainelDireito('pendencias');
      toast.warning(
        bloqueios.length === 1
          ? 'Falta um ponto para o fluxo rodar — veja a lista à direita.'
          : `Faltam ${bloqueios.length} pontos para o fluxo rodar — veja a lista à direita.`,
        { icon: <AlertTriangle className="w-4 h-4" /> },
      );
      return false;
    }
    try {
      await updateMutation.mutateAsync({ id: flowId, name: flowName, nodes: nodes as any, edges: edges as any });
      return true;
    } catch (err) {
      // O toast de erro sai no onError; aqui é só contexto pro DevTools.
      console.error('[saveFlowAsync] falha ao persistir fluxo', err);
      return false;
    }
  };

  const saveFlow = () => { void saveFlowAsync(); };

  // O simulador roda contra o que está no banco. Sem salvar antes, a pessoa
  // testaria a versão anterior achando que testou o que acabou de escrever.
  const ensureSavedBeforeTest = async (): Promise<boolean> => (hasUnsaved ? saveFlowAsync() : true);

  const handleOpenTestChat = async () => { if (await ensureSavedBeforeTest()) setShowTestChat(true); };
  const handleOpenTestSend = async () => { if (await ensureSavedBeforeTest()) setShowTestSend(true); };

  const handleBack = () => {
    if (hasUnsaved) { setConfirmLeaveOpen(true); return; }
    navigate('/whatsapp-flows');
  };

  // ─── Atalhos ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((meta && e.key === 'y') || (meta && e.shiftKey && e.key === 'z')) { e.preventDefault(); redo(); }
      if (meta && e.key === 's') { e.preventDefault(); saveFlow(); }
      if (e.key === 'Escape' && painelDireito === 'paleta') {
        setInserirNaAresta(null);
        setPainelDireito(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  /**
   * O Assistente enxerga o fluxo aberto no canvas e escreve nos nós.
   *
   * Aqui o contexto vale mais que em outras telas: o canvas guarda a versão
   * NÃO SALVA, e é sobre ela que a pessoa fala ("troca o texto desse nó").
   * Reler o fluxo do banco mostraria a versão anterior.
   *
   * Criar nó e ligar aresta ficam de fora: montar fluxo inteiro já é a tool
   * whatsapp_flow_create, que valida as saídas e as convenções do engine.
   * Aqui o gesto é editar o que está desenhado.
   */
  useContextoDaTela(
    {
      titulo: `Fluxo: ${flow?.name ?? "novo"}`,
      dados: {
        flowId,
        nome: flow?.name ?? null,
        status: flow?.status ?? null,
        gatilho: flow ? `${flow.triggerType}${flow.triggerValue ? `: ${flow.triggerValue}` : ""}` : null,
        alteracoesNaoSalvas: hasUnsaved,
        noSelecionado: selectedNode?.id ?? null,
        pendencias: pendencias.map((p) => ({ noId: p.noId, titulo: p.titulo })),
        nos: nodes.map((n) => ({
          id: n.id,
          tipo: n.type,
          rotulo: (n.data as { label?: string })?.label ?? null,
          texto:
            ((n.data as { messageContent?: { text?: string } })?.messageContent?.text ??
              (n.data as { promptMessage?: string; systemPrompt?: string })?.promptMessage ??
              (n.data as { systemPrompt?: string })?.systemPrompt ??
              "")?.slice(0, 300) || null,
          botoes: ((n.data as { messageContent?: { buttons?: Array<{ text: string }> } })?.messageContent?.buttons ?? []).map((b) => b.text),
        })),
        ligacoes: edges.map((e) => ({ de: e.source, para: e.target, saida: e.sourceHandle ?? "default" })),
      },
      acoes: [
        {
          id: "escrever_no_no",
          descricao:
            "Troca o texto de um nó do canvas pelo id em `nos`. Vale para mensagem, pergunta da coleta e prompt da IA; condição, espera, ação e fim não têm texto. Não salva o fluxo.",
          parametros: { noId: "id do nó", texto: "o novo texto" },
        },
        {
          id: "selecionar_no",
          descricao: "Abre um nó no painel de configuração, para o usuário ver o que você está falando.",
          parametros: { noId: "id do nó" },
        },
      ],
    },
    {
      escrever_no_no: (args) => {
        const alvo = nodes.find((n) => n.id === String(args.noId ?? ""));
        if (!alvo) throw new Error("Nó não encontrado — liste os nós de novo.");
        const texto = String(args.texto ?? "");
        const dados = alvo.data as Record<string, any>;
        // Nó que não fala (condição, espera, tag, webhook, classificador, fim)
        // não tem onde escrever: injetar messageContent ali sujava o rascunho,
        // gravava um campo inútil no Salvar e ainda devolvia sucesso.
        const novoDado =
          alvo.type === "ai_assistant"
            ? { ...dados, systemPrompt: texto }
            : alvo.type === "data_collection"
              ? { ...dados, promptMessage: texto }
              : alvo.type === "content" || alvo.type === "email_content"
                ? { ...dados, messageContent: { ...(dados.messageContent ?? { type: "text" }), text: texto } }
                : null;
        if (!novoDado) {
          throw new Error(
            `O nó "${alvo.type}" não tem texto (é condição, espera, ação ou fim). Para mudá-lo, use o painel de configuração.`,
          );
        }
        handleUpdateNode(alvo.id, novoDado);
        setSelectedNode({ ...alvo, data: novoDado });
      },
      selecionar_no: (args) => {
        const alvo = nodes.find((n) => n.id === String(args.noId ?? ""));
        if (!alvo) throw new Error("Nó não encontrado.");
        setSelectedNode(alvo);
        setPainelDireito('config');
      },
    },
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  if (flowId && isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const canUndo = historyRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  const arestaDeDestino = inserirNaAresta ? edges.find((e) => e.id === inserirNaAresta) : null;
  const destinoDaPaleta = arestaDeDestino
    ? `Entra no meio da ligação selecionada. Esc para cancelar.`
    : selectedNode
      ? `Entra depois de "${(selectedNode.data as { label?: string })?.label || 'bloco selecionado'}".`
      : nodes.length === 0
        ? 'Comece pelo passo que abre a conversa.'
        : 'Entra no fim do fluxo.';

  const arestasDesenhadas = edges.map((e) => ({
    ...e,
    type: 'deletable' as const,
    data: {
      aoInserir: (edgeId: string) => { setInserirNaAresta(edgeId); setPainelDireito('paleta'); },
      aoExcluir: excluirAresta,
    } satisfies AcoesDaAresta,
  }));

  /**
   * O cartão "Começa quando" e a seta dele para o primeiro passo.
   *
   * Vivem só no desenho: não entram em `nodes`/`edges`, não são salvos e não
   * chegam ao espelho do motor. Gravá-los ali significaria um nó de tipo
   * inexistente no enum e um `isStart` no lugar errado — e o gatilho de verdade
   * mora em `flows.triggerType`, não no grafo.
   */
  const nosDesenhados: Node[] = [
    cartaoDoGatilho,
    ...nodes.map((n) => ({ ...n, selected: selectedNode?.id === n.id })),
  ];

  const arestaDoGatilho: Edge[] = noDeEntrada
    ? [{
        id: '__gatilho__-entrada',
        source: ID_DO_GATILHO,
        sourceHandle: 'gatilho',
        target: noDeEntrada.id,
        // `default`, não `deletable`: não há o que inserir nem apagar numa
        // seta que só diz onde o fluxo começa.
        type: 'default',
        style: { strokeDasharray: '4 4', stroke: '#94a3b8' },
        focusable: false,
      }]
    : [];

  return (
    <PermissionGuard permission="whatsapp:manage_flows">
    <div className="h-screen flex flex-col bg-background">
      {/* Cabeçalho */}
      <div className="border-b px-4 py-2.5 flex items-center justify-between bg-card shadow-sm shrink-0 gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleBack} className="gap-1.5 shrink-0">
            <ArrowLeft className="w-4 h-4" />
            Fluxos
          </Button>
          <div className="h-4 w-px bg-border shrink-0" />
          <MessageSquare className="w-4 h-4 text-green-600 shrink-0" />
          <Input
            value={flowName}
            onChange={(e) => { setFlowName(e.target.value); setHasUnsaved(true); }}
            className="w-56 h-8 text-sm font-medium border-transparent hover:border-border focus:border-border"
            placeholder="Nome do fluxo"
          />
          {hasUnsaved && (
            <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-300 bg-yellow-50 shrink-0">
              Não salvo
            </Badge>
          )}
          <span className="text-xs text-muted-foreground shrink-0">
            {nodes.length} {nodes.length === 1 ? 'passo' : 'passos'}
          </span>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant={pendencias.length > 0 ? 'outline' : 'ghost'}
            size="sm"
            onClick={() => setPainelDireito(painelDireito === 'pendencias' ? null : 'pendencias')}
            className={`gap-1.5 ${bloqueios.length > 0 ? 'text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100' : 'text-muted-foreground'}`}
            title="O que ainda falta para o fluxo rodar"
          >
            {bloqueios.length > 0 ? (
              <><AlertTriangle className="w-4 h-4" />{bloqueios.length} {bloqueios.length === 1 ? 'pendência' : 'pendências'}</>
            ) : pendencias.length > 0 ? (
              <><AlertTriangle className="w-4 h-4" />{pendencias.length} {pendencias.length === 1 ? 'aviso' : 'avisos'}</>
            ) : (
              <><CheckCircle2 className="w-4 h-4 text-emerald-600" />Tudo certo</>
            )}
          </Button>

          <div className="h-4 w-px bg-border" />

          <Button
            variant={painelDireito === 'paleta' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => {
              if (painelDireito === 'paleta') { setPainelDireito(null); setInserirNaAresta(null); }
              else setPainelDireito('paleta');
            }}
            className="gap-1.5"
          >
            <Plus className="w-4 h-4" />
            Adicionar passo
          </Button>

          <Button variant="ghost" size="sm" onClick={organizar} disabled={nodes.length === 0} title="Arrumar o desenho em colunas" className="gap-1.5">
            <LayoutGrid className="w-4 h-4" />
            Organizar
          </Button>
          <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo} title="Desfazer (Ctrl+Z)" className="h-8 w-8 p-0">
            <Undo2 className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={redo} disabled={!canRedo} title="Refazer (Ctrl+Y)" className="h-8 w-8 p-0">
            <Redo2 className="w-4 h-4" />
          </Button>

          <div className="h-4 w-px bg-border" />

          <Button
            onClick={() => void handleOpenTestChat()}
            variant="outline" size="sm" className="gap-1.5"
            disabled={!flowId || nodes.length === 0 || updateMutation.isPending}
            title={!flowId ? 'Salve o fluxo antes de testar' : 'Simular a conversa — nada é enviado de verdade'}
          >
            <Play className="w-4 h-4 text-green-600" />
            Testar
          </Button>
          <Button
            onClick={() => void handleOpenTestSend()}
            variant="outline" size="sm" className="gap-1.5"
            disabled={!flowId || nodes.length === 0 || updateMutation.isPending}
            title={!flowId ? 'Salve o fluxo antes de testar' : 'Disparar o fluxo de verdade para um número'}
          >
            <Send className="w-4 h-4 text-orange-600" />
            Enviar teste
          </Button>

          <div className="h-4 w-px bg-border" />

          <Button onClick={saveFlow} size="sm" disabled={updateMutation.isPending} className="gap-1.5">
            <Save className="w-4 h-4" />
            {updateMutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </div>

      <TestChatPanel
        open={showTestChat}
        onOpenChange={setShowTestChat}
        flowId={flowId}
        flowName={flowName}
        onBeforeStart={ensureSavedBeforeTest}
      />
      <TestSendDialog open={showTestSend} onOpenChange={setShowTestSend} flowId={flowId} />

      {/* Gatilho de webhook: mostra a URL pronta para copiar. Some nos demais. */}
      {flow?.triggerType === "webhook" && flowId !== null && (
        <WebhookTriggerBanner flowId={flowId} flowName={flowName} />
      )}

      <div className="flex-1 relative flex overflow-hidden">
        <div
          className="flex-1 relative"
          ref={canvasRef}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={aoSoltarNoCanvas}
        >
          <ReactFlow
            nodes={nosDesenhados}
            edges={[...arestaDoGatilho, ...arestasDesenhadas]}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => {
              // O cartão do gatilho tem o próprio botão "Alterar"; clicar nele
              // não abre painel de configuração de nó, que ele não é.
              if (node.id === ID_DO_GATILHO) return;
              const atual = nodes.find((n) => n.id === node.id);
              if (atual) { setSelectedNode(atual); setPainelDireito('config'); }
            }}
            onEdgeDoubleClick={(_, edge) => excluirAresta(edge.id)}
            onPaneClick={() => {
              setSelectedNode(null);
              // A paleta e o painel do gatilho ficam: quem clicou no fundo
              // estava mirando o canvas, não fechando o que abriu de propósito.
              if (painelDireito === 'config') setPainelDireito(null);
            }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultViewport={{ x: 50, y: 50, zoom: 1 }}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1.2, minZoom: 0.5 }}
            deleteKeyCode={null}
            // A marca "React Flow" no canto não é exigência da licença (o pacote
            // é MIT) — é um pedido dos autores, que oferecem a assinatura Pro a
            // quem a remove.
            proOptions={{ hideAttribution: true }}
          >
            <Background />
            <Controls />
            <MiniMap />
          </ReactFlow>

          {inserirNaAresta && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-foreground/90 text-background text-xs px-3 py-1.5 shadow-lg">
              Escolha o passo na prateleira — ele entra no meio dessa ligação.
            </div>
          )}

          {nodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center max-w-xs">
                <p className="text-sm font-medium text-foreground">Este fluxo ainda está vazio</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">
                  Clique em <strong>Adicionar passo</strong>, no topo, para colocar o primeiro —
                  normalmente uma mensagem de boas-vindas.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 'config' sem nó selecionado não tem o que mostrar — sem esta guarda
            o painel abriria vazio depois de apagar o bloco aberto. */}
        {painelDireito && !(painelDireito === 'config' && !selectedNode) && (
          <PainelLateral>
          {painelDireito === 'paleta' && (
            <PaletaDeBlocos
              aoClicar={aoClicarNaPaleta}
              aoComecarArrasto={(b) => { blocoArrastadoRef.current = b; }}
              aoTerminarArrasto={() => { blocoArrastadoRef.current = null; }}
              aoFechar={() => { setPainelDireito(null); setInserirNaAresta(null); }}
              destino={destinoDaPaleta}
            />
          )}

          {painelDireito === 'gatilho' && (
            <PainelDeGatilho
              triggerType={flow?.triggerType}
              triggerValue={flow?.triggerValue}
              cooldownDias={(flow as { reentryCooldownDays?: number } | undefined)?.reentryCooldownDays}
              salvando={gatilhoMutation.isPending}
              aoSalvar={(tipo, valor, cooldown) => {
                if (!flowId) return;
                gatilhoMutation.mutate({
                  id: flowId,
                  triggerType: tipo,
                  triggerValue: valor,
                  reentryCooldownDays: cooldown,
                });
              }}
              aoFechar={() => setPainelDireito(null)}
            />
          )}

          {painelDireito === 'pendencias' && (
            <PainelDePendencias
              pendencias={pendencias}
              nos={nodes}
              aoIrPara={irParaNo}
              aoAbrirGatilho={() => setPainelDireito('gatilho')}
              aoFechar={() => setPainelDireito(null)}
            />
          )}

          {painelDireito === 'config' && selectedNode && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between shrink-0">
                <h2 className="text-sm font-semibold text-foreground">Configurações</h2>
                <Button
                  variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground"
                  onClick={() => { setSelectedNode(null); setPainelDireito(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <NodeConfigPanel
                  node={selectedNode}
                  onClose={() => { setSelectedNode(null); setPainelDireito(null); }}
                  onUpdate={handleUpdateNode}
                  onDelete={setNodeToDelete}
                  onSaveNode={() => { setSelectedNode(null); setPainelDireito(null); }}
                />
              </div>
            </div>
          )}
          </PainelLateral>
        )}

      </div>

      <AlertDialog open={confirmLeaveOpen} onOpenChange={setConfirmLeaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sair sem salvar?</AlertDialogTitle>
            <AlertDialogDescription>
              Você tem alterações que ainda não foram salvas neste fluxo. Se sair agora, vai perdê-las.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmLeaveOpen(false); navigate('/whatsapp-flows'); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sair sem salvar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!nodeToDelete} onOpenChange={(open) => { if (!open) setNodeToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover este passo?</AlertDialogTitle>
            <AlertDialogDescription>
              O passo e todas as suas ligações saem do fluxo. Dá para desfazer com Ctrl+Z.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteNode}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </PermissionGuard>
  );
}
